import "./App.css";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home/Home";
import Manage from "./pages/Manage/Manage";
import HowToUse from "./pages/HowToUse/HowToUse";
import AdminDashboard from "./pages/Manage/Admin/AdminDashboard";
import AdminDataTable from "./pages/Manage/Admin/AdminDataTable";
import AdminItemEditor from "./pages/Manage/Admin/AdminItemEditor";
import AdminCreateFlow from "./pages/Manage/Admin/AdminCreateFlow";
import AdminMetadata from "./pages/Manage/Admin/AdminMetadata";
import AdminTools from "./pages/Manage/Admin/AdminTools";
import AdminTracking from "./pages/Manage/Admin/AdminTracking";
import AdminHelp from "./pages/Manage/Admin/AdminHelp";
import CardTransparent from "./UI/Cards/CardTransparent/CardTransparent";
import Header from "./Components/Header/Header";
import { authActions } from "./store/authSlice";
import { ErrorBoundary } from "./HOC/ErrorHandling/ErrorBoundary/ErrorBoundary";
import LocalErrorDisplay from "./HOC/ErrorHandling/LocalErrorDisplay/LocalErrorDisplay";
import { useUserDataInit } from "./Hooks/useUserDataInit";
import { useRunGatherCatalogData } from "./Hooks/useRunGatherCatalogData";
import { useRunGatherContentData } from "./Hooks/useRunGatherContentData";
import {
  markAppDataFetched,
  useAppResumeRefresh,
} from "./Hooks/useAppResumeRefresh";
import useStandaloneExternalLinks from "./Hooks/useStandaloneExternalLinks";
import axios from "axios";
import { loadingRequestsActions } from "./store/loadingRequestsSlice";
import { statusUpdateActions } from "./store/statusUpdateSlice";
import { adminGatherUser } from "./data/cmsDraft";
import { pickContentByType } from "./data/contentPicker";
import { CmsPreviewProvider } from "./data/CmsPreviewContext";
import * as icons from "react-icons/fa";

function App() {
  // iOS home-screen: open off-site links outside the trapped in-app browser sheet
  useStandaloneExternalLinks();
  const fallbackArtistSocialLInks = [
    {
      name: "Spotify",
      profileLink: "https://open.spotify.com/user/313ndmlrf7icszoy3cat7wikxwqi",
      faIcon: "FaSpotify",
    },
    {
      name: "SoundCloud",
      profileLink: "https://soundcloud.com/ignite-elysium-music",
      faIcon: "FaSoundcloud",
    },
  ];

  const dispatch = useDispatch();
  const location = useLocation();
  const hidePublicHeader =
    location.pathname.startsWith("/manage") ||
    location.pathname.startsWith("/how-to-use");
  // const catalogData = GatherCatalogData();
  const catalog = useSelector((state) => state.catalogData);
  const contentData = useSelector((state) => state.contentData);
  if (
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development") &&
    Object.hasOwn(catalog, "catalog") &&
    catalog.catalog
  ) {
    console.log(
      "%cCatalog Data:",
      "color:#fff;background:#be6502;padding:5px;border-radius:0 25px 25px 0",
      catalog,
    );
    console.log(
      "%cContent Data:",
      "color:#fff;background:#007215;padding:5px;border-radius:0 25px 25px 0",
      contentData,
    );
  }
  const [user, setUser] = useState();
  const { userLoggedIn = user } = useSelector((state) => state.auth);
  const authUser = useSelector((state) => state.auth.user);
  const [localError, setLocalError] = useState({
    title: null,
    active: false,
    message: null,
  });
  const userDataInit = useUserDataInit();
  const [noDBErrors, setNoDBErrors] = useState({
    status: true,
    message: "All is well.",
  });
  const aboutIsActive = Boolean(
    pickContentByType(contentData.content, "about"),
  );

  ////////////////////////////////////////
  /// Network Communication
  ////////////////////////////////////////
  // Register interceptors once. Doing this during render stacked a new pair
  // on every App re-render and multiplied load-count + error dispatches.
  useEffect(() => {
    const reqId = axios.interceptors.request.use(
      (request) => {
        dispatch(loadingRequestsActions.addToLoadRequest());
        return request;
      },
      (error) => {
        dispatch(loadingRequestsActions.removeFromLoadRequest());
        return Promise.reject(error);
      },
    );

    const resId = axios.interceptors.response.use(
      (response) => {
        const serverRateLimitRemaining =
          response.headers["ratelimit-remaining"];
        if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
          console.log(
            "%cRate Limit Remaining: ",
            "color:#fff;background:#ccd62d;padding:5px;border-radius:0 25px 25px 0",
            serverRateLimitRemaining,
          );
        }
        dispatch(loadingRequestsActions.removeFromLoadRequest());
        dispatch(
          statusUpdateActions.updateStatus({
            status: response.status,
            statusText: response.statusText,
            rateLimitRemaining: serverRateLimitRemaining,
          }),
        );
        setTimeout(() => {
          dispatch(authActions.resetRecentLogout());
          dispatch(authActions.resetRecentLogin());
        }, 3000);

        return response;
      },
      (error) => {
        dispatch(loadingRequestsActions.removeFromLoadRequest());
        console.log(
          "%cERROR:",
          "color:#f0f0ef;background:#ff0000;padding:32px;border-radius:0 25px 25px 0",
          error,
        );
        dispatch(
          statusUpdateActions.updateStatus({
            status:
              Object.hasOwn(error, "response") && error.response.status
                ? error.response.status
                : 500,
            statusText:
              Object.hasOwn(error, "response") && error.response.statusText
                ? error.response.statusText
                : error.message,
          }),
        );
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [dispatch]);

  ////////////////////////////////////////
  /// FUNCTIONALITY
  ////////////////////////////////////////
  const runGatherCatalogData = useRunGatherCatalogData();
  const runGatherContentData = useRunGatherContentData();

  // Single path for cold start + rare soft re-fetch (stale after 24h on resume).
  // Cold start is anonymous until auth hydrates; resume passes the admin JWT
  // so draftPayload is not wiped after redaction.
  const refreshAppData = useCallback(() => {
    userDataInit({ setLocalError, setUser });
    const gatherUser = adminGatherUser(authUser);
    runGatherCatalogData({ user: gatherUser, setLocalError });
    runGatherContentData({ user: gatherUser, setLocalError });
    markAppDataFetched();
  }, [userDataInit, runGatherCatalogData, runGatherContentData, authUser]);

  // iOS home-screen / standalone: soft re-fetch if data is ≥24h old, hard-reload
  // on bfcache restore, hard-reload when /version.json shows a new deploy.
  useAppResumeRefresh({ onSoftRefresh: refreshAppData });

  ////////////////////////////////////////
  /// EFFECTS
  ////////////////////////////////////////

  // Initial user & data setup (same gather path as soft re-fetch).
  // Run once on mount only ; resume refreshes are handled by useAppResumeRefresh.
  useEffect(() => {
    refreshAppData();
  }, []);

  // Re-gather when an admin session appears or disappears so /manage sees
  // draftPayload after login and visitors stay on the redacted snapshot after logout.
  // Gather fns are new every render — keep them in refs so this effect only
  // runs on actual auth changes (otherwise it storms the API and trips the
  // backend 200-req/10min limiter; CORS-less 429s show up as ERR_NETWORK).
  const didSkipFirstAuthGather = useRef(false);
  const gatherCatalogRef = useRef(runGatherCatalogData);
  const gatherContentRef = useRef(runGatherContentData);
  gatherCatalogRef.current = runGatherCatalogData;
  gatherContentRef.current = runGatherContentData;
  useEffect(() => {
    if (!didSkipFirstAuthGather.current) {
      didSkipFirstAuthGather.current = true;
      return;
    }
    const gatherUser = adminGatherUser(authUser);
    gatherCatalogRef.current({ user: gatherUser, setLocalError });
    gatherContentRef.current({ user: gatherUser, setLocalError });
  }, [authUser?.token, authUser?.isAdmin]);

  // Register error if catalog DB not accessible.
  useEffect(() => {
    if (localError.active) {
      setNoDBErrors({
        status: false,
        message: localError.message,
      });
    }
  }, [localError]);

  useEffect(() => {
    if (
      (userLoggedIn && !process.env.NODE_ENV) ||
      process.env.NODE_ENV === "development"
    )
      setUser(userLoggedIn);
    if (userLoggedIn !== "not logged in") dispatch(authActions.logIn(user));
  }, [userLoggedIn]);

  useEffect(() => {
    if (!noDBErrors.status)
      dispatch(
        statusUpdateActions.updateStatus({
          status: 500,
          statusText: noDBErrors.message,
        }),
      );
  }, [noDBErrors.status]);

  useEffect(() => {
    if (!noDBErrors.status)
      dispatch(
        statusUpdateActions.updateStatus({
          status: 500,
          statusText: noDBErrors.message,
        }),
      );
  }, [noDBErrors.status]);

  ////////////////////////////////////////
  /// Output
  ////////////////////////////////////////
  return (
    <CmsPreviewProvider>
    <div className="App">
      {!hidePublicHeader && (
        <ErrorBoundary>
          <header className="App-header">
            <CardTransparent>
              <Header aboutIsActive={aboutIsActive} musicIsActive={true} />
            </CardTransparent>
          </header>
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        {localError.active && (
          <div className="local-error-container">
            <div className="local-error-inner-wrap">
              <LocalErrorDisplay
                title={localError.title}
                message={localError.message}
              />
              <div className="local-error-social">
                <p className="local-error-social-label">
                  Or listen on the platforms while we sort this out:
                </p>
                <ul className="error-modal-link-wrap">
                  {fallbackArtistSocialLInks.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.profileLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {icons[item.faIcon]} {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <Routes>
          <Fragment>
            <Fragment>
              <Route
                path="/manage"
                element={
                  <Manage
                    noDBErrors={noDBErrors}
                    setNoDBErrors={setNoDBErrors}
                  />
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route
                  path="catalog"
                  element={<AdminDataTable section="catalog" />}
                />
                <Route
                  path="catalog/new"
                  element={<AdminCreateFlow section="catalog" />}
                />
                <Route
                  path="catalog/edit/:id"
                  element={<AdminItemEditor section="catalog" />}
                />
                <Route
                  path="content"
                  element={<AdminDataTable section="content" />}
                />
                <Route
                  path="content/new"
                  element={<AdminCreateFlow section="content" />}
                />
                <Route
                  path="content/edit/:id"
                  element={<AdminItemEditor section="content" />}
                />
                <Route path="metadata" element={<AdminMetadata />} />
                <Route path="tracking" element={<AdminTracking />} />
                <Route path="tools" element={<AdminTools />} />
                <Route path="help" element={<AdminHelp />} />
              </Route>
              <Route path="/how-to-use" element={<HowToUse />} />
              <Route
                path="/connected"
                element={
                  <Home
                    noDBErrors={noDBErrors}
                    setNoDBErrors={setNoDBErrors}
                    aboutIsActive={aboutIsActive}
                    musicIsActive={true}
                  />
                }
              />
              <Route
                path="/"
                element={
                  <Home
                    noDBErrors={noDBErrors}
                    setNoDBErrors={setNoDBErrors}
                    aboutIsActive={aboutIsActive}
                    musicIsActive={true}
                  />
                }
              />
              <Route
                path="*"
                element={
                  <Home
                    notFound={true}
                    noDBErrors={noDBErrors}
                    setNoDBErrors={setNoDBErrors}
                    aboutIsActive={aboutIsActive}
                    musicIsActive={true}
                  />
                }
              />
            </Fragment>
          </Fragment>
        </Routes>
      </ErrorBoundary>
    </div>
    </CmsPreviewProvider>
  );
}

export default App;
